/**
 * When to do the work: traffic and weather, joined.
 *
 * The platform answers two halves of one question separately — `displacement`
 * finds the windows in which closing a position strands nobody, `weather`
 * finds the windows in which the work can physically and legally be done — and
 * `insight/planning.py` intersects them. This is the shape that comes back and
 * the small amount of reasoning a screen needs on top of it.
 *
 * THE DISTINCTION THIS FILE EXISTS TO PRESERVE. There are three outcomes, not
 * two, and collapsing them is the mistake the whole module is built to avoid:
 *
 *   found        there is a window; here it is
 *   no window    both halves were checked and there isn't one
 *   NOT KNOWN    a half could not be assessed, so there is no answer at all
 *
 * The third is the one that gets lost. A window clear on one constraint and
 * unchecked on the other looks exactly like a window clear on both, and the
 * platform's own comment records a console that rendered an unassessable half
 * as "blocked", in red, beside a verdict saying the opposite. So `known` is
 * carried per half as well as jointly, and `outcome()` below refuses to reduce
 * the three to a boolean.
 */

/** One continuous span in which something is true. */
export interface PlanWindow {
  from: string;
  to: string;
}

/** One half of the answer — traffic, or weather — with its own state. */
export interface PlanSide {
  /** Whether a window was found. Meaningless unless `known`. */
  found: boolean;
  /**
   * Whether this half could be assessed at all. False means no data, not a
   * negative answer, and the two must never be drawn the same way.
   */
  known: boolean;
  windows: PlanWindow[];
  whyNot?: string;
  /** Traffic only: the least damaging window, when none is free. */
  leastBad?: unknown;
  /** Weather only: what stopped it, as [reason, count] pairs. */
  blocking?: [string, number][];
  /** Weather only: which records the answer was built from. */
  assessedFrom?: string[];
}

export interface PlanAnswer {
  position: string;
  category: string;
  hoursNeeded: number;
  traffic: PlanSide;
  weather: PlanSide;
  found: boolean;
  known: boolean;
  whyNot?: string;
  /** Present only when found. */
  windows: PlanWindow[];
  windowCount?: number;
  earliest?: PlanWindow;
  verdict?: string;
  caveat?: string;
  weatherBlockers?: [string, number][];
}

export type PlanOutcome = "found" | "no-window" | "unknown";

/**
 * Which of the three outcomes this is.
 *
 * `known` is checked FIRST and independently of `found`. Reading `found` first
 * and treating false as "no window" is exactly how the unknown case disappears:
 * an unassessable answer also has `found: false`.
 */
export function outcome(a: PlanAnswer): PlanOutcome {
  if (!a.known) return "unknown";
  return a.found ? "found" : "no-window";
}

/** The same three states for one half, so each can be drawn honestly. */
export function sideOutcome(side: PlanSide): PlanOutcome {
  if (!side.known) return "unknown";
  return side.found ? "found" : "no-window";
}

export const OUTCOME_LABEL: Record<PlanOutcome, string> = {
  found: "Window found",
  "no-window": "No window",
  unknown: "Cannot be assessed",
};

/**
 * How long a window is, in hours.
 *
 * Returns null rather than 0 for an unparseable pair. A duration of zero is a
 * statement about the window; null is a statement about the data, and a crew
 * booked against a silent zero is the failure this distinction prevents.
 */
export function windowHours(w: PlanWindow): number | null {
  const a = Date.parse(w.from);
  const b = Date.parse(w.to);
  if (!Number.isFinite(a) || !Number.isFinite(b) || b <= a) return null;
  return (b - a) / 3_600_000;
}

/**
 * Which side is responsible for there being no window.
 *
 * Only meaningful once the answer is known. An unknown half is not a blocker —
 * it is an absence — and naming it as one would put the blame for a missing
 * plan on a constraint nobody has measured.
 */
export function blockedBy(a: PlanAnswer): ("traffic" | "weather")[] {
  if (!a.known) return [];
  const out: ("traffic" | "weather")[] = [];
  if (a.traffic.known && !a.traffic.found) out.push("traffic");
  if (a.weather.known && !a.weather.found) out.push("weather");
  return out;
}

/**
 * The case worth its own sentence: both halves have windows, none overlap.
 *
 * Neither constraint is at fault and the job still cannot be done, which reads
 * as a contradiction unless it is named. A planner seeing "traffic: clear" and
 * "weather: clear" beside "no window" will assume the screen is broken.
 */
export function isNoOverlap(a: PlanAnswer): boolean {
  return (
    a.known && !a.found && a.traffic.found && a.weather.found
  );
}
